import { useCallback, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import {
  applyPositionEditorSave,
  type EditorPosition,
  type ProvisionalPositionDraft,
} from "@/pages/dokumente/documentPositionModel";

/**
 * Neue Bausteine öffnen den Positionsdialog, ohne schon im Dokument zu stehen.
 * Abbrechen verwirft sie. Übernehmen fügt sie an der Drop-Stelle ein.
 * Ein späteres Bearbeiten bleibt beim Abbrechen erhalten.
 */
export function useProvisionalPositionEditor(
  positions: EditorPosition[],
  setPositions: Dispatch<SetStateAction<EditorPosition[]>>,
) {
  const [editLocalId, setEditLocalId] = useState<string | null>(null);
  const [provisional, setProvisional] = useState<ProvisionalPositionDraft | null>(null);
  const provisionalRef = useRef<ProvisionalPositionDraft | null>(null);

  const openPosition = useCallback((localId: string, draft?: ProvisionalPositionDraft) => {
    if (draft) {
      provisionalRef.current = draft;
      setProvisional(draft);
    } else {
      provisionalRef.current = null;
      setProvisional(null);
    }
    setEditLocalId(localId);
  }, []);

  const closePositionEditor = useCallback(() => {
    provisionalRef.current = null;
    setProvisional(null);
    setEditLocalId(null);
  }, []);

  const savePosition = useCallback(
    (next: EditorPosition) => {
      const pending = provisionalRef.current;
      const isNew = pending != null && pending.position.localId === next.localId;
      if (isNew) {
        provisionalRef.current = null;
        setProvisional(null);
      }
      setPositions((prev) => applyPositionEditorSave(prev, isNew ? pending : null, next));
    },
    [setPositions],
  );

  const editPosition = useMemo(() => {
    if (provisional && provisional.position.localId === editLocalId) return provisional.position;
    return positions.find((row) => row.localId === editLocalId) ?? null;
  }, [editLocalId, positions, provisional]);

  return {
    editLocalId,
    editPosition,
    openPosition,
    closePositionEditor,
    savePosition,
  };
}
